import {Injectable, NgZone} from '@angular/core';
import {Observable, of, take} from 'rxjs';
import {LookupService} from '../../common/service/lookup.service';
import {UserPreferencesService} from '../../common/service/user-preferences.service';
import {MapToolLookup} from '../../common/models/map-tool.model';
import {WindowWidgetsService} from '../window-widgets/window-widgets.service';

/* eslint-disable @typescript-eslint/no-explicit-any */
@Injectable({
    providedIn: 'root'
})
export class MapToolsService {
    title = 'MapToolsService';

    constructor(
        private lookupService: LookupService,
        private userPreferencesService: UserPreferencesService,
        private windowWidgetsService: WindowWidgetsService,
        private ngZone: NgZone
    ) {}

    private mapToolLookups: MapToolLookup[] = [];
    private toolGroupMap: Record<string, string[]> = {};
    private toolMap: Record<string, any> = {};
    private shortcutsEnabled = true;
    private activatePendingList = [];
    private initPendingList = [];

    isScaleActive() {
        return this.isActive('SCALE');
    }

    isMoveActive() {
        return this.isActive('MOVE') && this.isScaleActive();
    }

    isRotateActive() {
        return this.isActive('ROTATE') && this.isScaleActive();
    }

    getToolsInGroup(toolOrTools: string | string[]): string[] {
        if (Array.isArray(toolOrTools)) {
            return toolOrTools
                .reduce((acc, tool) => {
                    acc.push(...this.getToolsInGroup(tool).filter(t => !acc.includes(t)));
                    return acc;
                }, [])
                .filter((v, i, a) => a.indexOf(v) === i);
        }

        const tools = [];

        if (this.toolGroupMap[toolOrTools]) {
            this.toolGroupMap[toolOrTools].forEach(t => tools.push(...this.getToolsInGroup(t)));
        }
        tools.push(toolOrTools);

        return tools.filter((v, i, a) => a.indexOf(v) === i);
    }

    getToolsPendingActivation(): string[] {
        return Object.keys(this.toolMap).filter(t => this.isPendingActivation(t));
    }

    getActiveTools(): string[] {
        return Object.keys(this.toolMap).filter(t => this.isActive(t));
    }

    getSuspendedTools(): string[] {
        return Object.keys(this.toolMap).filter(t => this.isSuspended(t));
    }

    getToolsToDeactivate(id: string): string[] {
        const tools = [];

        const lookup = this.mapToolLookups.find(l => l.id === id);
        if (lookup) {
            tools.push(...lookup.toolsToDeactivate);
            if (lookup.group) {
                tools.push(...this.getToolsToDeactivate(lookup.group));
            }
        }

        return tools.filter((v, i, a) => a.indexOf(v) === i);
    }

    getToolsToSuspend(id: string): string[] {
        const tools = [];

        const lookup = this.mapToolLookups.find(l => l.id === id);
        if (lookup) {
            tools.push(...lookup.toolsToSuspend);
            if (lookup.group) {
                tools.push(...this.getToolsToSuspend(lookup.group));
            }
        }

        return tools.filter((v, i, a) => a.indexOf(v) === i);
    }

    isWindow(id: string) {
        return id.endsWith('_WINDOW');
    }

    isUndockedWindow(id: string) {
        return (
            this.isWindow(id) &&
            (this.windowWidgetsService.isOpen(id) ||
                (this.isInactive(id) && this.userPreferencesService.userPreferences?.undockedWindows.includes(id)))
        );
    }

    isPendingActivation(id: string) {
        return this.toolMap[id] && this.toolMap[id].pendingActivation;
    }

    isActive(id: string) {
        return this.toolMap[id] && this.toolMap[id].deactivate && this.toolMap[id].state === 'ACTIVE';
    }

    isSuspended(id: string) {
        return this.toolMap[id] && this.toolMap[id].state === 'SUSPENDED';
    }

    isInactive(id: string) {
        return !this.isActive(id) && !this.isSuspended(id);
    }

    isActiveOrSuspended(id: string) {
        return this.isActive(id) || this.isSuspended(id);
    }

    isDisabled(id: string) {
        return this.toolMap[id] && this.toolMap[id].disabled && this.toolMap[id].disabled();
    }

    toggle(id: string) {
        this.ngZone.runOutsideAngular(() => {
            console.debug(`${this.title} - toggle - id: ${id}`);

            if (this.isDisabled(id) && (!this.isWindow(id) || this.isInactive(id))) {
                console.info(`${this.title} - toggle: ${id} is disabled`);
                return;
            }
            if (this.isInactive(id)) {
                this.activate(id).pipe(take(1)).subscribe();
            } else {
                this.deactivate(id);
            }
        });
    }

    init(id?: string, fnActivate?: (...args: any[]) => any, fnDeactivate?: (...args: any[]) => any, fnDisabled?: (...args: any[]) => any) {
        this.ngZone.runOutsideAngular(() => {
            console.info(`${this.title} - init: ${id}`);
            if (arguments.length === 0) {
                if (this.mapToolLookups.length === 0) {
                    // Populate lookups
                    this.mapToolLookups.push(
                        ...this.lookupService.getLookups('mapTool').map((l: MapToolLookup) => {
                            l.toolsToDeactivate = l.toolsToDeactivate || [];
                            l.toolsToSuspend = l.toolsToSuspend || [];
                            l.shortcutKeyGroups = l.shortcut ? l.shortcut.split(',').map(v => v.split('+')) : [[]];
                            return l;
                        })
                    );

                    this.init('END');

                    // Populate toolGroupMap
                    this.mapToolLookups
                        .filter(l => l.group)
                        .reduce((acc, l) => {
                            if (!acc[l.group]) {
                                acc[l.group] = [];
                            }
                            acc[l.group].push(l.id);
                            return acc;
                        }, this.toolGroupMap);

                    // Run pending init calls
                    this.initPendingList.splice(0, this.initPendingList.length).forEach(args => this.init(...args));

                    // Run pending activate calls
                    this.activatePendingList
                        .splice(0, this.activatePendingList.length)
                        .forEach((args: any) => this.activate(args[0], args[1], args[2], args[3]).pipe(take(1)).subscribe());
                }

                // Reset tools to "INACTIVE"
                Object.keys(this.toolMap).forEach(t => (this.toolMap[t].state = 'INACTIVE'));
                return;
            }

            if (this.mapToolLookups.length === 0) {
                // eslint-disable-next-line prefer-rest-params
                this.initPendingList.push([...(arguments as any)]);
                return;
            }

            if (this.isActive(id)) {
                this.deactivate(id, false, id);
            }

            this.toolMap[id] = {
                activate: fnActivate || (() => {
                }),
                deactivate: fnDeactivate || null,
                disabled: fnDisabled || null,
                state: 'INACTIVE'
            };
        });
    }

    restoreSuspendedTools() {
        this.getSuspendedTools().forEach(t => this.restore(t));
    }

    activateTool(id: string) {
        const tool = this.toolMap[id];
        if (tool.deactivate) {
            // Only set tool state to ACTIVE | SUSPENDED when it has a deactivation handler
            tool.state = this.getToolsToSuspendWithActiveTools().includes(id) ? 'SUSPENDED' : 'ACTIVE';
            tool.pendingActivation = false;
        }

        this.restoreSuspendedTools();

        return tool.activate();
    }

    getActiveOrPendingActivationTools() {
        return [...this.getActiveTools(), ...this.getToolsPendingActivation()].filter((v, i, a) => a.indexOf(v) === i);
    }

    getToolsToDeactivateWithActiveTools() {
        return this.getActiveOrPendingActivationTools()
            .reduce((acc, t) => {
                acc.push(...this.getToolsInGroup(this.getToolsToDeactivate(t)));
                return acc;
            }, [])
            .filter((v, i, a) => a.indexOf(v) === i);
    }

    getToolsToSuspendWithActiveTools() {
        return this.getActiveOrPendingActivationTools()
            .reduce((acc, t) => {
                acc.push(...this.getToolsInGroup(this.getToolsToSuspend(t)));
                return acc;
            }, [])
            .filter((v, i, a) => a.indexOf(v) === i);
    }

    getToolsIncompatibleWithActiveTools() {
        return [...this.getToolsToDeactivateWithActiveTools(), ...this.getToolsToSuspendWithActiveTools()].filter((v, i, a) => a.indexOf(v) === i);
    }

    getActiveToolsThatSuspend(id: string) {
        return this.getActiveTools().filter(t => this.getToolsToSuspend(t).includes(id));
    }

    deactivateIncompatibleTools(id: string) {
        // Deactivate active tools and groups
        const toolsToDeactivate = this.getToolsToDeactivate(id).filter(t => !(this.isUndockedWindow(id) && this.isWindow(t)));
        toolsToDeactivate.forEach(t => this.deactivate(t, false, id));

        // Suspend active tools and groups
        const toolsToSuspend = this.getToolsToSuspend(id);
        toolsToSuspend.forEach(t => this.deactivate(t, true, id));

        return [...toolsToDeactivate, ...toolsToSuspend];
    }

    activate(
        id: string,
        fnActivate?: (...args: any[]) => any,
        fnDeactivate?: (...args: any[]) => any,
        fnDisabled?: (...args: any[]) => any
    ): Observable<any> {
        console.info(`${this.title} - activate: ${id}`);

        if (this.mapToolLookups.length === 0) {
            // eslint-disable-next-line prefer-rest-params
            this.activatePendingList.push([...arguments]); // Save pending activation
            return of(null); // Observable of no operation
        }

        if (arguments.length > 1) {
            this.init(id, fnActivate, fnDeactivate, fnDisabled);
        }

        const lookup = this.mapToolLookups.find(l => l.id === id);
        if (!lookup) {
            console.error(`${this.title} - ${id} not found in lookup`);
            return of(null); // Return an observable with no action
        }

        // Case 3: Handle disabled tools
        if (this.isDisabled(id)) {
            console.info(`${this.title} - activate: ${id} is disabled`);
            return of(null);
        }

        if (this.isActive(id)) {
            this.deactivate(id, false, id);
        }

        // Case 5: Deactivate incompatible tools
        const incompatibleTools = this.deactivateIncompatibleTools(id)
            .reduce((acc, t) => {
                acc.push(...this.getToolsInGroup(t));
                return acc;
            }, [])
            .filter(t => !this.isUndockedWindow(t));

        if (incompatibleTools.length === 0) {
            // No incompatible tools, activate tool directly
            return of(this.activateTool(id));
        }

        this.toolMap[id].pendingActivation = true;

        this.activateTool(id);
        return of(true);
    }

    deactivate(id: string, suspend?: boolean, activatedTool?: string) {
        console.info(`${this.title} - deactivate - toolId: ${id}, suspend: ${suspend}, activatedTool: ${activatedTool || ''}`);
        if (this.toolGroupMap[id]) {
            this.toolGroupMap[id].forEach(t => this.deactivate(t, suspend, activatedTool));
        }

        const tool = this.toolMap[id];

        if (!tool || tool.state === 'INACTIVE') {
            return;
        }

        if (this.isWindow(id)) {
            if (activatedTool && this.isUndockedWindow(id) && !suspend) {
                // Leave undocked window open if it's deactivation is due to activation of another tool
                return;
            }

            // Deactivate group associated with the window being deactivated
            const groupName = id.substring(0, id.length - '_WINDOW'.length);
            if (this.toolGroupMap[groupName]) {
                this.deactivate(groupName, suspend, activatedTool);
            }
        }

        if (suspend) {
            tool.state = 'SUSPENDED';
        } else {
            tool.state = 'INACTIVE';
        }

        if (tool.deactivate) {
            tool.deactivate(suspend);
        }

        if (activatedTool) {
            // Don't restore suspended tools if deactivation is due to activation of another tool
            return;
        }

        this.restoreSuspendedTools();
    }

    suspend(id: string) {
        console.info(`${this.title} - suspend: ${id}, toolsInGroup:`, this.getToolsInGroup(id));

        this.getToolsInGroup(id).forEach(t => {
            const tool = this.toolMap[t];
            if (tool && tool.deactivate && tool.state === 'ACTIVE') {
                tool.deactivate(true);
                tool.state = 'SUSPENDED';
            }
        });
    }

    restore(id: any, options: any = {}) {
        console.info(`${this.title} - restore: ${id}`);

        // Only restore tool if it's compatible with active tools
        const toolsIncompatibleWithActiveTools = this.getToolsIncompatibleWithActiveTools();

        const toolsToRestore = this.getToolsInGroup(id).filter((t: any) => this.isSuspended(t) && !toolsIncompatibleWithActiveTools.includes(t));

        if (toolsToRestore.length) {
            toolsToRestore.forEach((t: any) => {
                const tool = this.toolMap[t];
                tool.activate(true);
                tool.state = 'ACTIVE';
            });
            if (Object.hasOwn(options, 'redraw') ? options.redraw : true) {
                // Trigger a redraw so associated UI elements reflect the current active state instead of the previous suspended state
            }
        }
    }

    getShortcut(id: string) {
        return this.mapToolLookups.filter(l => l.id === id).map(l => l.shortcut)[0] || '';
    }

    getTooltipWithShortcut(id: string, tooltip: string) {
        return [tooltip, this.getShortcut(id), this.isSuspended(id) && 'Tool Suspended (Click to unsuspend)'].filter(v => v).join(' | ');
    }

    shortcutKeyDisabled(e: KeyboardEvent, activeElement: Element) {
        return (
            activeElement &&
            ((activeElement.tagName === 'INPUT' && !['checkbox', 'radio'].includes((activeElement as HTMLInputElement).type)) ||
                activeElement.tagName === 'SELECT' ||
                activeElement.tagName === 'TEXTAREA') &&
            (!(e.ctrlKey || e.metaKey) || e.code === 'KeyA')
        );
    }

    setShortcutsEnabled(enabled: boolean) {
        this.shortcutsEnabled = enabled;
    }

    isVisible = (element?: Element) => {
        if (!element) {
            return false;
        }
        const style = getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    };

    keydownHandler(e: KeyboardEvent) {
        if (!this.shortcutsEnabled) {
            return;
        }
        const mainMap = document.querySelector('#mainMap');
        const ngBusy = document.querySelector('div.ng-busy');

        // if (!e.originalEvent.repeat && $('#mainMap').is(':visible') && !$uibModalStack.getTop() && $('div[cg-busy] > div.cg-busy').is(':hidden')) {
        if (!e.repeat && this.isVisible(mainMap) && !this.isVisible(ngBusy)) {
            if (this.shortcutKeyDisabled(e, document.activeElement)) {
                return;
            }

            let tool: string;

            if (['Alt', 'Control', 'Meta', 'Shift'].includes(e.key)) {
                tool = this.mapToolLookups.filter(l => l.shortcutKeyGroups.some(g => g.length === 1 && g.includes(e.key))).map(l => l.id)[0];
            } else if (e.code) {
                tool = this.mapToolLookups
                    .filter(l =>
                        l.shortcutKeyGroups.some(
                            g =>
                                g.length &&
                                (g.includes('Alt') ? e.altKey : !e.altKey) &&
                                (g.includes('Ctrl') ? e.ctrlKey || e.metaKey : !(e.ctrlKey || e.metaKey)) &&
                                (g.includes('Shift') ? e.shiftKey : !e.shiftKey) &&
                                g.filter(k => !['Alt', 'Ctrl', 'Shift'].includes(k)).some(k => k === e.key || `Key${k}` === e.code)
                        )
                    )
                    .map(l => l.id)[0];
            }

            if (tool) {
                e.preventDefault();
                this.toggle(tool);
            }
        } else {
            if (e.code === 'KeyS' && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey) {
                e.preventDefault();
            }
        }
    }

    keyupHandler(e: KeyboardEvent) {
        const mainMap = document.querySelector('#mainMap');
        const ngBusy = document.querySelector('div.ng-busy');
        if (mainMap && this.isVisible(mainMap) && !this.isVisible(ngBusy)) {
            if (e.key === 'Alt' && this.isActive('PAN')) {
                this.deactivate('PAN');
            }
        }
    }

    /**
     * Used by external components to configure the activate function of a tool, if that function is not available in this service
     * @param id the tool id
     * @param fn the activate function
     */
    setActivateFnForTool(id: string, fn: (...args: any[]) => any) {
        const tool = this.toolMap[id];
        if (!tool) {
            return;
        }
        tool.activate = fn;
    }
}
